import React from "react";

import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LockIcon from "@mui/icons-material/Lock";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import UpdateIcon from "@mui/icons-material/Update";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Menu } from "components/popups";
import { Dayjs } from "dayjs";
import { useFormatDateHook, useModals } from "hooks";
import { useDateFormatSelect } from "store";

type ConnectionItem = {
  id: string | number;
  title: string;
  username: string;
  subtitle: string | null;
  status: "loading" | "2FA error" | "error" | "ok";
  last_connected?: string;
  last_index?: string;
  last_sync?: string;
  mfa?: boolean;
  logo?: string;
  enable_auto_sync?: boolean;
};

export interface ConnectionCardProps {
  item: ConnectionItem;
  onClick: () => void;
  onConnect: () => void;
  onDelete: () => void;
  onToggleAutoSync?: () => void;
}
export const ConnectionCard: React.FC<ConnectionCardProps> = ({ item, onClick, onConnect, onDelete, onToggleAutoSync }) => {
  const { confirmModal } = useModals();
  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDate = (date?: string | number | Date | Dayjs | null | undefined) => {
    return formatDateHook({ date, format: dateFormat });
  };
  const handleDelete = () => {
    confirmModal({
      msg: `Are you sure you want to remove the dataroom "${item.title}" for account "${item.username}"?`,
      title: "Remove Dataroom",
      action: onDelete
    });
  };

  return (
    <Stack
      sx={{
        width: 200,
        height: 210,
        border: "1px solid #dae0e2",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#FFFFFF",
        color: "#0a3142",
        fontSize: "16px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.2s ease-in-out",
        ":hover": {
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)"
        }
      }}
    >
      {/* Header Section */}
      <Stack
        sx={{
          p: "8px 12px",
          borderBottom: "1px solid #f1f5f9",
          backgroundColor: "transparent",
          position: "relative",
          minHeight: "75px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
        }}
        onClick={onClick}
      >
        {/* Logo positioned at left */}
        <Stack
          sx={{
            position: "absolute",
            top: "8px",
            left: "8px",
            zIndex: 1,
            width: "50px",
            height: "50px",
            backgroundColor: "transparent",
            borderRadius: "4px",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden"
          }}
        >
          {item.logo ? (
            <img
              src={item.logo}
              alt={`${item.title} logo`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain"
              }}
              onLoad={() => {
                console.log(`Logo loaded successfully for ${item.title}:`, item.logo);
              }}
              onError={(e) => {
                console.error(`Logo failed to load for ${item.title}:`, item.logo, e);
                // Hide the image if it fails to load
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
          {/* Only show text fallback when no logo is provided */}
          {!item.logo && (
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#64748b",
                textTransform: "uppercase",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)"
              }}
            >
              {item.title.substring(0, 2)}
            </Typography>
          )}
        </Stack>

        {/* Title and subtitle positioned to the right of logo */}
        <Stack
          sx={{
            position: "absolute",
            top: "8px",
            left: "65px",
            right: "8px",
            alignItems: "flex-start",
            justifyContent: "flex-start"
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#0a3142",
              textAlign: "left"
            }}
          >
            {item.title}
          </Typography>
          {item.subtitle && (
            <Typography
              title={item.subtitle}
              sx={{
                fontSize: "13px",
                color: "#64748b",
                textAlign: "left",
                fontStyle: "italic",
                mt: "-5px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%"
              }}
            >
              {item.subtitle}
            </Typography>
          )}
        </Stack>

        {/* Bottom right elements ordered left to right: lock, status, menu */}
        <Stack
          sx={{
            position: "absolute",
            bottom: "4px",
            right: "4px",
            zIndex: 1,
            flexDirection: "row",
            gap: "2px",
            alignItems: "center"
          }}
        >
          {/* Lock */}
          {item.mfa && (
            <span title="MFA Enabled">
              <LockIcon
                sx={{
                  fontSize: 16,
                  color: "#9e9e9e"
                }}
              />
            </span>
          )}
          
          {/* Auto Sync */}
          {item.enable_auto_sync  && (
            <span title="Daily Sync Enabled">
              <UpdateIcon
                sx={{
                  fontSize: 16,
                  color: "#9e9e9e"
                }}
              />
            </span>
          )}

          {/* Status */}
          {item.status === "loading" ? (
            <span title="Checking connection...">
              <Box sx={{ display: "inline-flex" }}>
                <CircularProgress size={16} />
              </Box>
            </span>
          ) : item.status === "2FA error" ? (
            <span title="Two-Factor Authentication Required">
              <WarningIcon sx={{ fill: "#cd6f00", fontSize: "16px" }} />
            </span>
          ) : (
            <span title={item.status === "ok" ? "Connected" : "Connection Error"}>
              <CloudDoneIcon
                sx={{
                  fill: item.status === "ok" ? "#0F9D58" : "#9e9e9e",
                  fontSize: "16px"
                }}
              />
            </span>
          )}

          {/* Menu */}
          <span title="More options" style={{ display: "inline-flex" }}>
            <Menu
              iconButtonProps={{ size: "sm" }}
              icon={<MoreVertIcon fontSize="md" />}
              options={[
                {
                  title: "Reconnect",
                  icon: <RefreshIcon sx={{ fontSize: "20px" }} />,
                  onClick: onConnect
                },
                {
                  title: item.enable_auto_sync ? "Disable Daily Sync" : "Enable Daily Sync",
                  icon: <UpdateIcon sx={{ fontSize: "20px" }} />,
                  onClick: onToggleAutoSync
                },
                {
                  title: "Remove dataroom",
                  icon: <ExitToAppIcon sx={{ fontSize: "20px" }} />,
                  onClick: handleDelete
                }
              ]}
            />
          </span>
        </Stack>
      </Stack>

      {/* Content Section */}
      <Stack
        sx={{
          flex: 1,
          p: "12px 16px",
          gap: "12px",
          backgroundColor: "transparent",
          borderTop: "1px solid #e2e8f0",
          boxShadow: "inset 0 2px 6px rgba(0, 0, 0, 0.08)"
        }}
      >
        {/* Username Section */}
        <Stack sx={{ alignItems: "center" }}>
          <Typography
            title={item.username}
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              fontSize: "15px",
              fontWeight: 500,
              textAlign: "center",
              maxWidth: "160px",
              color: "#0a3142"
            }}
          >
            {item.username}
          </Typography>
        </Stack>

        {/* Status Details */}
        <Stack sx={{ gap: 0 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>Latest Login:</Typography>
            <Typography sx={{ fontSize: "12px", color: "#0a3142", fontWeight: 500 }}>
              {item.last_connected ? formatDate(item.last_connected) : "N/A"}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>Latest Scan:</Typography>
            <Typography sx={{ fontSize: "12px", color: "#0a3142", fontWeight: 500 }}>
              {item.last_index ? formatDate(item.last_index) : "N/A"}
            </Typography>
          </Stack>

          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "12px", color: "#64748b", fontWeight: 400 }}>Last Sync:</Typography>
            <Typography sx={{ fontSize: "12px", color: "#0a3142", fontWeight: 500 }}>
              {item.last_sync ? formatDate(item.last_sync) : "N/A"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
